/**
 * /zh/living-documentation — LivingDocumentation 的中文版。
 * 英文原版见 LivingDocumentation.tsx。
 */
import { MarketingShell } from './MarketingShell';

export function LivingDocumentationZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/living-documentation"
      documentTitle="不漂移的活文档 · Draftlize"
      documentDescription="活文档与它描述的系统保持同步。大多数文档几周内就漂移。Draftlize 从 card graph 派生文档，上游决策一变就自动把文档标成 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 活文档</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 60px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            活文档，<br/>
            <em>比这个 sprint 活得更久。</em>
          </h1>
          <p className="lede">
            活文档（living documentation）指的是与它所描述的系统保持同步、而不是写完那天就冻住的文档。敌人是<em>文档漂移</em>：文档发布时是准的，产品往前走，几周内它就悄悄地、很自信地错了——而读者分不清哪份新、哪份旧。常见的补救——一张 review 清单、一个文档 sprint、更多自律——每次都输给「发版」。Draftlize 走另一条路：文档从 card graph <em>派生</em>而来，每份都带着它由哪些卡片生成的快照，一旦上游某个决策变了，文档就自己标成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">静态文档 vs <em>活的文档。</em></h2>
          </div>
          <p>每份文档在上线那天都是准的。问题在第 90 天——它能不能告诉你它过时了，还是只会继续读着像真的。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>wiki / Confluence / markdown</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>文档从哪来</td><td className="ours yes">从来源卡片派生</td><td className="partial">手打，然后脱钩</td></tr>
            <tr><td>某个来源决策变了</td><td className="ours yes">文档自动标 stale</td><td className="no">文档悄悄变错</td></tr>
            <tr><td>一眼能不能信？</td><td className="ours yes">stale 是标出来的，不用猜</td><td className="no">你得对着代码重新核</td></tr>
            <tr><td>AI agent 能靠它吗？</td><td className="ours yes">经 MCP 读当前卡片</td><td className="partial">读到什么文本算什么</td></tr>
            <tr><td>谁让它保持最新</td><td className="ours yes">substrate 追踪漂移</td><td className="no">一个永远不来的文档 sprint</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">文档为什么会<em>漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">写一次，之后再不维护</h3>
            <p>文档在写下那天最完美，之后更新它就是永远的苦差。维护跟发版抢时间，输了，于是文档冻在原地、系统继续往前——每次发布，缝就更宽一点。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">文档和它底下的决策没有连接</h3>
            <p>一页讲计费怎么算的文档，和当初定下计费规则的那个<a href="/zh/decision-log" style={{ color: 'var(--amber)' }}>决策</a>之间没有任何连接。改了规则，没人告诉这页它现在是虚构。依赖在现实里存在，在工具里却不存在。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">读者分不清新旧</h3>
            <p>漂移最糟的地方是它看不见。一份过时的文档和一份最新的长得一模一样，于是读者信了它——而一份很自信地错着的文档，比没有文档更贵。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Draftlize 怎么让文档<em>一直活着。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">文档从卡片派生</h3>
            <p>一份设计文档、一份<a href="/zh/technical-documentation-template" style={{ color: 'var(--amber)' }}>技术文档</a>、或一份 spec，是从底下的卡片——决策、需求、理由——生成的，而不是当作独立散文手打的。文档有真正的来源，不只是有个作者。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">每份文档带着来源快照</h3>
            <p>文档生成时，它记下自己来自哪些卡片、在什么时候。正是这份快照，让系统能把文档拿去和那些卡片的当前状态比对——文档与真相之间的链是被存下来的，不是靠记的。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">卡片一变，文档标 stale</h3>
            <p>一个后台检测器盯着来源卡片。改动一张某份文档所依赖的 <a href="/zh/architecture-decision-record" style={{ color: 'var(--amber)' }}>ADR</a> 或 spec，凡是从它派生的文档都标 stale——于是文档主动告诉你它该看一眼了，而不是等着被发现是错的。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          问题从来不是把文档写出来，而是文档没法知道自己什么时候不再成立。
        </blockquote>
        <cite>让文档从决策派生。决策一动，文档就说话。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是活文档（living documentation）？', a: '活文档是与它所描述的系统保持同步、而不是写完那刻就冻住的文档。这个词来自 BDD 圈，那里文档是从可执行的规格生成的。更广义的想法是：文档应该从一个活的来源派生出来，于是来源一变，它要么自动更新、要么把自己标出来。' },
            { q: '什么是文档漂移（documentation drift）？', a: '文档漂移是指：系统在演进、文档没跟上，两者之间裂开的那道缝。它危险，是因为一份漂移的文档和一份最新的长得一模一样，读者会继续信它——一份很自信地错着的文档，比没有文档更贵。' },
            { q: '怎么让文档一直保持最新？', a: '持久的解法是别再手动维护文档，而是从单一来源派生它，再在来源变化时把它标出来。Draftlize 从 card graph 生成文档，并记下来源卡片的快照，于是一个后台检测器能在上游决策一动那一刻，就把文档标成 stale。' },
            { q: '这和 docs-as-code 有什么不同？', a: 'Docs-as-code 把文档放进版本控制、和代码一起走同一条流水线——这是真实的进步，但文档仍然是一段得靠人记着去更新的散文。放在 card graph 上的活文档补上了缺的那一环：文档知道自己依赖哪些决策，于是它能自己标 stale，而不是指望有人注意到。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">会自己标出漂移的文档<em>——用 $5 免费。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账户送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把决策和 spec 存成卡片，从它们派生文档，让每份文档在来源决策一动时就标 stale。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
